import { AppDispatch } from 'app/store';
import { API_ENDPOINTS } from 'utils/constants';
import { deleteAPI, getAPI, postAPI, putAPI } from 'services/api/apiAsync';
import fetchAfterDelay from 'services/api/delayedFetch';
import { fetchUserDetails } from 'services/user/userAsync';
import {
  setPlanEntitlements,
  setAvailableOffers,
  setPlanChangeEligibility,
  setRawPlanFeatures,
} from './planSlice';
import type { IPlanFeature } from './types';
import { ICancelRequest, IPlanPiiRequirements } from './types';

/* Angular parity — mirrors `UserPlanService.getUserPlan()`
 * (`apps/igww/src/app/user/user-plan.service.ts:321-323`) which hits
 * `USER_PLAN_URL = '/client/plans/'`. The response carries a
 * `features` array (`{name, childFeature, code, status, ...}`) that
 * Angular scans at `user-plan.service.ts:91-96` to derive
 * `dark_web_child` and `ssn_trace` child-feature entitlements.
 *
 * On success we persist that array into `plan.rawPlanFeatures` so the
 * child-feature selectors (`selectHasDarkWebChildPlanFeature`,
 * `selectHasSsnTraceChildPlanFeature`) can resolve synchronously
 * without any component re-fetching the same endpoint. */

/* Module-level in-flight guard for `GET /api/client/plans/`.
 * `useSessionInitialSetup`'s `fetchPlanFeatures` effect fires on
 * `userId`, and React 18 StrictMode double-mounts the layout in
 * dev — without this guard both mounts hit the network before the
 * slice can populate. Same pattern as `fetchWatchlistItems` /
 * `fetchBillingAccountInfo` / `fetchCRTB`. The flag clears on
 * settle, so any post-login refresh (sign-in → sign-out → sign-in
 * as a different user) still fires fresh. */
let userPlanInFlight: Promise<unknown> | null = null;

export function getUserPlan() {
  return async (dispatch: AppDispatch) => {
    if (userPlanInFlight) return userPlanInFlight;

    userPlanInFlight = (async () => {
      try {
        const config = {
          url: `${API_ENDPOINTS.planDetails}`,
        };
        const response = (await dispatch(getAPI(config)).unwrap()) as {
          features?: IPlanFeature[];
        };
        if (Array.isArray(response?.features)) {
          dispatch(setRawPlanFeatures(response.features));
        }
        return response;
      } catch (error) {
        return Promise.reject(error);
      } finally {
        userPlanInFlight = null;
      }
    })();

    return userPlanInFlight;
  };
}

/** Same endpoint as `getUserPlan`, but for a named plan rather than the member's current one. */
export function getAdditionalPiiDetails(planCode: string) {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: `${API_ENDPOINTS.planDetails}${planCode}`,
      };
      const response: IPlanPiiRequirements = await dispatch(
        getAPI(config),
      ).unwrap();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function findPlanDetails(planCode: string) {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.findPlan,
        data: {
          plan_code: planCode,
        },
      };
      const response = await dispatch(postAPI(config)).unwrap();
      /* Dispatched even when the plan declares none, so `entitlementsLoaded` latches and a
         zero-entitlement plan stops looking unfetched. */
      dispatch(setPlanEntitlements(response[0]?.entitlements ?? {}));
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function fetchAvailableOffers() {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.getAvailableOffers,
      };
      const response = await dispatch(getAPI(config)).unwrap();
      dispatch(setAvailableOffers(response ?? []));
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function fetchPlanChangeEligibility() {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.eligibility,
      };
      const response = await dispatch(getAPI(config)).unwrap();
      if (response) {
        dispatch(setPlanChangeEligibility(response));
      }
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function submitPlanChange(offerCode: string) {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: `${API_ENDPOINTS.changeMarketingOffer}/${offerCode}`,
        data: {},
      };
      const response = await dispatch(putAPI(config)).unwrap();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function cancelPlan(userId: string, requestReason: ICancelRequest) {
  /* No try/catch here: there is no value to return and nothing to add to the
     error, so letting the rejection propagate is identical for the caller — the
     orchestrator already wraps this dispatch to raise the error toast — and it
     avoids a placeholder return just to satisfy `consistent-return`. */
  return async (dispatch: AppDispatch) => {
    const config = {
      url: `${API_ENDPOINTS.userProfile}/${userId}`,
      data: requestReason,
    };

    /**
     * Success is *any* resolved response, matching identityWeb
     * (`account-cancel-modal.component.ts:82-95`), which is the functional
     * authority. Angular puts its whole success path in the RxJS `next` handler and
     * raises the banner only from `error`, so every 2xx confirms the cancellation.
     * axios draws the same line — it rejects 4xx and 5xx — so "resolved" here means
     * "2xx", exactly as in Angular.
     *
     * This replaces a React-only status allowlist. BRD FR-038 / AC-022 / decision
     * D10 rejected anything but `202 Accepted`, on the theory that another 2xx meant
     * the cancellation had not really happened. Browser verification on 2026-08-21
     * falsified it: the live endpoint answers **`204 No Content`**, so a genuinely
     * accepted cancellation was rejected — the member saw "Membership cannot be
     * cancelled. Please try again.", no refetch fired, and Billing details kept
     * showing an active membership while the account moved to
     * `pending cancellation` server-side. A retry then "failed" again against an
     * already-cancelled account. Every earlier pass stubbed a 202, which is why the
     * gate read as correct until a real cancellation was performed.
     *
     * Requester direction (2026-08-21) is to match Angular rather than keep a status
     * whitelist. That also makes this immune to the same defect should the endpoint
     * ever answer 200 or 201.
     *
     * ⚠ BRD FR-038 / AC-022 need an amendment to record this.
     */
    await dispatch(deleteAPI(config)).unwrap();

    /**
     * Refreshed unconditionally on success — a deliberate improvement on Angular,
     * which gates only this refresh on `res.status === 202` and so leaves
     * `user.status` stale on the real 204 until something else refetches.
     * Eligibility is refreshed alongside the user record because it drives the entry
     * link's disabled state (BRD FR-002 / FR-032 / BR-024); Angular refreshes it
     * here too, unconditionally (`:89`).
     *
     * `allSettled` is deliberate: a refresh failure must never turn a successful
     * cancellation into a failed one. The member *is* cancelled, so a stale card is
     * the lesser evil against an error banner.
     *
     * Delayed because the DELETE resolves before the cancellation is persisted, so an
     * immediate refetch can still read an active account.
     */
    await dispatch(
      fetchAfterDelay([fetchUserDetails(userId), fetchPlanChangeEligibility()]),
    );
  };
}
